import React, { useState } from 'react';
import { Save, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { apiCall, useAdminData, ImageUploader } from './_helpers';

const HeroManager = ({ onMessage }) => {
  const [hero, setHero, refetch] = useAdminData('hero', {});
  const [saving, setSaving] = useState(false);

  const updateField = (field, value) => setHero({ ...hero, [field]: value });

  const slides = hero.slides || [];
  const updateSlide = (idx, patch) => {
    const next = slides.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setHero({ ...hero, slides: next });
  };
  const addSlide = () => setHero({ ...hero, slides: [...slides, { image_url: '', alt: '', order: slides.length + 1, is_active: true }] });
  const removeSlide = (idx) => setHero({ ...hero, slides: slides.filter((_, i) => i !== idx) });
  const moveSlide = (idx, dir) => {
    const next = [...slides];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    next.forEach((s, i) => (s.order = i + 1));
    setHero({ ...hero, slides: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      // ensure orders are sequential
      const cleaned = { ...hero, slides: (hero.slides || []).map((s, i) => ({ ...s, order: i + 1 })) };
      await apiCall('PUT', 'hero', cleaned);
      onMessage('success', 'Hero saved');
      refetch();
    } catch (e) {
      onMessage('error', e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-md">
        <h2 className="text-2xl font-bold mb-6">Hero Section · Text & Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <input type="text" value={hero.title || ''} onChange={(e) => updateField('title', e.target.value)} className="w-full border rounded-lg px-4 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Slogan</label>
            <input type="text" value={hero.slogan || ''} onChange={(e) => updateField('slogan', e.target.value)} className="w-full border rounded-lg px-4 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Location</label>
            <input type="text" value={hero.location || ''} onChange={(e) => updateField('location', e.target.value)} className="w-full border rounded-lg px-4 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Slide Interval (ms)</label>
            <input type="number" value={hero.slide_interval_ms || 3000} onChange={(e) => updateField('slide_interval_ms', parseInt(e.target.value) || 3000)} className="w-full border rounded-lg px-4 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Transition Duration (ms)</label>
            <input type="number" value={hero.transition_duration_ms || 1500} onChange={(e) => updateField('transition_duration_ms', parseInt(e.target.value) || 1500)} className="w-full border rounded-lg px-4 py-2" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Slideshow ({slides.length} slides)</h2>
          <button onClick={addSlide} className="bg-[#C9A86C] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#B8956A]">
            <Plus size={18} /> Add Slide
          </button>
        </div>

        <div className="space-y-4">
          {slides.map((slide, idx) => (
            <div key={idx} className="border rounded-lg p-4 flex gap-4" data-testid={`hero-slide-row-${idx}`}>
              <div className="flex-shrink-0 w-32">
                {slide.image_url ? (
                  <img src={slide.image_url} alt={slide.alt} className="w-32 h-24 object-cover rounded" />
                ) : (
                  <div className="w-32 h-24 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs">No image</div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <ImageUploader currentImage={slide.image_url} onUpload={(url) => updateSlide(idx, { image_url: url })} label="Upload Slide" />
                <input type="text" placeholder="Image URL (or upload above)" value={slide.image_url || ''} onChange={(e) => updateSlide(idx, { image_url: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                <input type="text" placeholder="Alt text" value={slide.alt || ''} onChange={(e) => updateSlide(idx, { alt: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={slide.is_active !== false} onChange={(e) => updateSlide(idx, { is_active: e.target.checked })} />
                  Active
                </label>
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button onClick={() => moveSlide(idx, -1)} disabled={idx === 0} className="p-2 hover:bg-gray-100 rounded disabled:opacity-30"><ArrowUp size={16} /></button>
                <button onClick={() => moveSlide(idx, 1)} disabled={idx === slides.length - 1} className="p-2 hover:bg-gray-100 rounded disabled:opacity-30"><ArrowDown size={16} /></button>
                <button onClick={() => removeSlide(idx)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
          {slides.length === 0 && <p className="text-gray-500 text-center py-8">No slides yet. Add one above.</p>}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="bg-[#C9A86C] text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-[#B8956A] disabled:opacity-50" data-testid="hero-save-btn">
        <Save size={18} /> {saving ? 'Saving…' : 'Save Hero'}
      </button>
    </div>
  );
};

export default HeroManager;
